import React from "react";
import { createPortal } from "react-dom";
import { Palette } from "../holidayConfig";
import { range, seeded } from "./sceneUtils";
import { toPath, zigzag } from "./Storm";
import { useToonId } from "./toon";
import { STRIKE_BUILDUP_MS } from "../events";

/*
 * Halloween-finalen: mitt under första varvets sista bild i hjulet slår en
 * blixt ner i toppen av änkehusets tornspira, och sedan flammar texten upp
 * ovanför huset i blixtskrift. Blixten slocknar men texten blir kvar och
 * knastrar svagt.
 *
 * Nedslaget ska verkligen fånga uppmärksamheten. Tidslinje (scenes.css):
 *  - 0–1,2 s: hela skärmen, hjulet också, sjunker ner i mörker som under ett
 *    åskmoln, med en ljuskägla kvar runt huset. Kornblixtar flimrar svagt i
 *    himlen.
 *  - 1,2 s (IMPACT_MS): blixten slår ner i spiran och två sidoblixtar i
 *    marken intill. Skärmen blixtrar vitt två gånger (högst tre blixtar per
 *    sekund), bakgrunden rycker till, hjulet skakar, huset lyser upp och en
 *    dubbel tryckvåg och gnistregn går ut från spiran.
 *  - 1,4 s: texten slår ner vitglödgad och lägger sig.
 *
 * Ritas i scenens lagerkoordinater (W x H) med (x, y) = spirans topp.
 * Lagrets svg har overflow: visible, så blixten kan börja ovanför lagret.
 * Mörkret och skärmblixten ligger i en portal på body, ovanpå hjulet.
 * Varje nedslag får ett nytt `strike`-nummer och spelas då upp från början.
 *
 * Allt som bara ska synas under själva nedslaget har opacitet 0 som grund
 * och tänds av animationerna. Utan animationer (reducerad rörelse) syns
 * alltså bara texten.
 */

const LINES = ["Halloween på", "Enkehuset 28/10", "16.30"];
const FONT_SIZE = 42;
const LINE_HEIGHT = FONT_SIZE * 1.12;
/** Sista radens baslinje ovanför spirans topp. */
const TEXT_LIFT = 44;
/** Avstånd till skärmens högra kant som texten alltid håller (scenenheter). */
const EDGE = 24;
/** Luft mellan texten och hjulets kant, i skärmpixlar. */
const WHEEL_GAP = 16;
/** Texten krymps som mest till den här storleken för att få plats. */
const MIN_SCALE = 0.45;
const FONT = "Impact, 'Anton', 'Arial Black', sans-serif";
/** När blixten slår ner, räknat från att nedslaget börjar. */
const IMPACT_MS = STRIKE_BUILDUP_MS;
/** Hur länge hjulet skakar och bakgrunden rycker till (scenes.css). */
const QUAKE_MS = 950;

interface Props {
  x: number;
  y: number;
  p: Palette;
  strike: number;
}

const FinalStrike: React.FC<Props> = ({ x, y, p, strike }) => {
  const id = useToonId("fs");
  const coreRef = React.useRef<SVGTextElement>(null);
  // Textblockets bredd mäts efter första ritningen; uppskattningen räcker till dess.
  const [textWidth, setTextWidth] = React.useState(Math.max(...LINES.map(l => l.length)) * FONT_SIZE * 0.45);
  // Ytan där texten syns: från hjulets högra kant till skärmens högra kant, i
  // scenens koordinater. Hjulet ligger ovanpå bakgrunden och skulle annars
  // dölja en del av texten på smalare skärmar.
  const [bounds, setBounds] = React.useState({ left: 0, right: 1600 - EDGE });
  // Spirans läge på skärmen, där ljuskäglan och skärmblixten utgår ifrån.
  const [spot, setSpot] = React.useState<{ x: number; y: number } | null>(null);
  React.useLayoutEffect(() => {
    const el = coreRef.current;
    if (el && typeof el.getBBox === "function") {
      const w = el.getBBox().width;
      if (w > 0) setTextWidth(w);
    }
    const fit = () => {
      const ctm = el?.ownerSVGElement?.getScreenCTM();
      if (!ctm || !ctm.a) return;
      const toScene = (px: number) => (px - ctm.e) / ctm.a;
      const wheel = document.querySelector(".WheelSvg")?.getBoundingClientRect();
      setBounds({
        left: wheel ? toScene(wheel.right + WHEEL_GAP) : 0,
        right: Math.min(1600, toScene(window.innerWidth)) - EDGE,
      });
      setSpot({ x: ctm.a * x + ctm.e, y: ctm.d * y + ctm.f });
    };
    fit();
    window.addEventListener("resize", fit);
    return () => window.removeEventListener("resize", fit);
  }, [x, y]);

  // Hjulet skakar och bakgrunden rycker till när blixten slår ner.
  React.useEffect(() => {
    const root = document.documentElement;
    const start = window.setTimeout(() => root.classList.add("hol-final-quake"), IMPACT_MS);
    const stop = window.setTimeout(() => root.classList.remove("hol-final-quake"), IMPACT_MS + QUAKE_MS);
    return () => {
      window.clearTimeout(start);
      window.clearTimeout(stop);
      root.classList.remove("hol-final-quake");
    };
  }, [strike]);

  // Blixten: ny bana för varje nedslag, men alltid ända ner i spirans topp.
  // En andra, lite förskjuten bana blixtrar till i återslagen.
  const rnd = seeded(1879 + strike * 7);
  const main = zigzag(rnd, x - 70 + rnd() * 50, y - 2200, x, y, 30, 100);
  const branches = [11, 16, 20, 24].map(i => {
    const [bx, by] = main[i];
    const dir = i % 2 ? -1 : 1;
    return zigzag(rnd, bx, by, bx + dir * (70 + rnd() * 110), by + 100 + rnd() * 90, 7, 36);
  });
  const echo = zigzag(rnd, x + 40 + rnd() * 40, y - 2200, x + 2, y - 2, 28, 80);
  // Två sidoblixtar slår ner i marken på var sida om huset i samma ögonblick.
  const ground = y + 222;
  const sides = [x - 330, x + 150].map(gx => ({
    gx,
    d: toPath(zigzag(rnd, gx + (rnd() - 0.5) * 140, y - 2200, gx, ground, 24, 80)),
  }));
  const boltD = toPath(main);
  const branchD = branches.map(toPath).join(" ");
  const echoD = toPath(echo);
  const bothD = `${boltD} ${branchD}`;

  // Textblocket centreras över tornet, men flyttas in (och krymps vid behov)
  // så att det ryms mellan hjulet och skärmkanten (skenet räknas med).
  const pad = 20;
  const total = textWidth + 2 * pad;
  const k = Math.max(MIN_SCALE, Math.min(1, (bounds.right - Math.max(0, bounds.left)) / total));
  const half = (total / 2) * k;
  const cx = Math.min(bounds.right - half, Math.max(x, bounds.left + half));
  const base = y - TEXT_LIFT;
  const firstBase = base - (LINES.length - 1) * LINE_HEIGHT;
  const top = firstBase - FONT_SIZE * 0.78;
  const middle = (top + base) / 2;
  const under = base + 10;
  // Skalas runt sista radens mitt, så att blocket står kvar ovanför tornet.
  const fitText = `translate(${cx} ${base}) scale(${k}) translate(${-cx} ${-base})`;

  const crackleRnd = seeded(45);
  const underline = toPath(zigzag(crackleRnd, cx - textWidth / 2, under, cx + textWidth / 2, under, 26, 7));
  // Ett knastrande band från texten ner till spiran.
  const tetherX = Math.min(cx + half * 0.7, Math.max(cx - half * 0.7, x));
  const tether = toPath(zigzag(crackleRnd, tetherX, base + 12 * k, x, y - 4, 7, 16));
  // Små gnistbågar som slår till runt bokstäverna då och då.
  const left = cx - textWidth / 2;
  const right = cx + textWidth / 2;
  const arcs = [
    { x0: left - 6, y0: top + 6, x1: left + 36, y1: top - 6, dur: 3.1, delay: -0.4 },
    { x0: cx - 10, y0: top - 4, x1: cx + 34, y1: top + 4, dur: 4.3, delay: -2.1 },
    { x0: right - 30, y0: base + 14, x1: right + 10, y1: base + 4, dur: 5.7, delay: -3.3 },
  ].map(a => ({ ...a, d: toPath(zigzag(crackleRnd, a.x0, a.y0, a.x1, a.y1, 5, 14)) }));

  /** Textblocket, en tspan per rad. Ritas i flera lager för skenet. */
  const lines = (props: React.SVGProps<SVGTextElement> & { ref?: React.Ref<SVGTextElement> }) => (
    <text textAnchor="middle" fontFamily={FONT} fontSize={FONT_SIZE} letterSpacing={1.5} strokeLinejoin="round" {...props}>
      {LINES.map((line, i) => (
        <tspan key={i} x={cx} y={firstBase + i * LINE_HEIGHT}>
          {line}
        </tspan>
      ))}
    </text>
  );

  return (
    <g pointerEvents="none">
      <defs>
        <radialGradient id={`${id}-glow`}>
          <stop offset="0%" stopColor="#ffffff" stopOpacity="1" />
          <stop offset="14%" stopColor="#fff4dc" stopOpacity="0.75" />
          <stop offset="45%" stopColor={p[300]} stopOpacity="0.22" />
          <stop offset="100%" stopColor={p[500]} stopOpacity="0" />
        </radialGradient>
        <radialGradient id={`${id}-house`}>
          <stop offset="0%" stopColor="#fffaf0" stopOpacity="0.7" />
          <stop offset="60%" stopColor="#ffe8c8" stopOpacity="0.3" />
          <stop offset="100%" stopColor="#ffe8c8" stopOpacity="0" />
        </radialGradient>
        <radialGradient id={`${id}-back`}>
          <stop offset="0%" stopColor="#0b0402" stopOpacity="0.72" />
          <stop offset="55%" stopColor="#0b0402" stopOpacity="0.45" />
          <stop offset="100%" stopColor="#0b0402" stopOpacity="0" />
        </radialGradient>
        <linearGradient id={`${id}-core`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#ffffff" />
          <stop offset="60%" stopColor="#fff8e8" />
          <stop offset="100%" stopColor="#ffe3b8" />
        </linearGradient>
        <filter id={`${id}-bloom`} x="-50%" y="-10%" width="200%" height="120%">
          <feGaussianBlur stdDeviation="14" />
        </filter>
        <filter id={`${id}-halo`} x="-30%" y="-60%" width="160%" height="220%">
          <feGaussianBlur stdDeviation="7" />
        </filter>
        <filter id={`${id}-aura`} x="-40%" y="-80%" width="180%" height="260%">
          <feGaussianBlur stdDeviation="16" />
        </filter>
      </defs>

      {/* Själva nedslaget – monteras om och spelas upp vid varje nytt nedslag */}
      <g key={strike}>
        <ellipse className="hol-final-houselight" cx={x} cy={y + 150} rx="250" ry="140" fill={`url(#${id}-house)`} />
        <circle className="hol-final-glow" cx={x} cy={y} r="420" fill={`url(#${id}-glow)`} />
        <g fill="none" strokeLinecap="round" strokeLinejoin="round">
          <g className="hol-final-bolt">
            <path className="hol-final-draw" pathLength={1} d={bothD} stroke={p[200]} strokeWidth="44" opacity="0.4" filter={`url(#${id}-bloom)`} />
            <path className="hol-final-draw" pathLength={1} d={bothD} stroke="#fff1d6" strokeWidth="16" opacity="0.85" filter="url(#toon-softer)" />
            <path className="hol-final-draw" pathLength={1} d={bothD} stroke="#ffffff" strokeWidth="7" opacity="0.9" filter="url(#toon-soft)" />
            <path className="hol-final-draw" pathLength={1} d={branchD} stroke="#ffffff" strokeWidth="2.6" />
            <path className="hol-final-draw" pathLength={1} d={boltD} stroke="#ffffff" strokeWidth="5.5" />
          </g>
          <g className="hol-final-side">
            {sides.map(({ gx, d }) => (
              <g key={gx}>
                <path className="hol-final-draw" pathLength={1} d={d} stroke={p[200]} strokeWidth="22" opacity="0.35" filter="url(#toon-softer)" />
                <path className="hol-final-draw" pathLength={1} d={d} stroke="#ffffff" strokeWidth="3" />
                <ellipse cx={gx} cy={ground} rx="70" ry="14" fill="#fff4dc" opacity="0.6" filter="url(#toon-softer)" />
              </g>
            ))}
          </g>
          <g className="hol-final-echo">
            <path d={echoD} stroke="#fff1d6" strokeWidth="12" opacity="0.7" filter="url(#toon-softer)" />
            <path d={echoD} stroke="#ffffff" strokeWidth="3.5" />
          </g>
        </g>
        <circle className="hol-final-ring" cx={x} cy={y} r="9" fill="none" stroke="#fff6dc" strokeWidth="4" vectorEffect="non-scaling-stroke" />
        <circle className="hol-final-ring hol-final-ring-late" cx={x} cy={y} r="9" fill="none" stroke={p[200]} strokeWidth="2.5" vectorEffect="non-scaling-stroke" />
        {range(20).map(i => (
          <g key={i} transform={`rotate(${i * 18 + 5} ${x} ${y})`}>
            <path
              className="hol-final-spark"
              d={`M${x} ${y - 6} V${y - 18 - (i % 4) * 5}`}
              stroke={i % 2 ? "#ffffff" : p[100]}
              strokeWidth={i % 3 ? 2 : 2.8}
              strokeLinecap="round"
              style={{ animationDelay: `${IMPACT_MS + (i % 5) * 30}ms` }}
            />
          </g>
        ))}
        <g className="hol-final-ember">
          <circle cx={x} cy={y} r="22" fill={p[200]} opacity="0.6" filter="url(#toon-softer)" />
          <circle cx={x} cy={y} r="4.5" fill="#ffffff" />
        </g>
      </g>

      {/* Blixtskriften ovanför huset. Den blir kvar efter nedslaget. */}
      <g key={`text-${strike}`} className="hol-final-text">
        <path className="hol-final-tether" d={tether} fill="none" stroke="#ffffff" strokeWidth="1.4" strokeLinejoin="round" />
        <g transform={fitText}>
          {/* Mörk rök bakom texten, så att den syns även framför månen */}
          <ellipse cx={cx} cy={middle} rx={textWidth / 2 + pad + 60} ry={(base - top) / 2 + 46} fill={`url(#${id}-back)`} />
          <g className="hol-final-live">
            <g transform={`translate(${cx} ${base}) skewX(-8) translate(${-cx} ${-base})`}>
              {/* Brett sken runt hela texten */}
              {lines({ fill: p[400], stroke: p[400], strokeWidth: 18, opacity: 0.4, filter: `url(#${id}-aura)` })}
              {/* Yttre sken som andas */}
              <g className="hol-final-pulse" filter={`url(#${id}-halo)`}>
                {lines({ fill: p[300], stroke: p[300], strokeWidth: 9 })}
                <path d={underline} stroke={p[300]} strokeWidth="6" fill="none" />
              </g>
              {/* Vitglödgad kant närmast bokstäverna */}
              {lines({ fill: "#ffffff", stroke: "#ffffff", strokeWidth: 4, opacity: 0.85, filter: "url(#toon-soft)" })}
              {/* Bokstäverna: vit kärna med en varm kontur bakom för skärpa */}
              {lines({ ref: coreRef, fill: `url(#${id}-core)`, stroke: p[400], strokeWidth: 2.4, paintOrder: "stroke" })}
              <path d={underline} stroke="#ffffff" strokeWidth="1.8" fill="none" strokeLinejoin="round" />
            </g>
          </g>
          {arcs.map((a, i) => (
            <path
              key={i}
              className="hol-final-arc"
              d={a.d}
              fill="none"
              stroke="#ffffff"
              strokeWidth="1.1"
              strokeLinejoin="round"
              style={{ animationDuration: `${a.dur}s`, animationDelay: `${a.delay}s` }}
            />
          ))}
        </g>
      </g>

      {/* Mörkret och skärmblixten ovanpå hela sidan, med ljuskäglan runt huset */}
      {spot &&
        createPortal(
          <div key={strike} className="hol-final-overlay" aria-hidden="true">
            <div
              className="hol-final-dim"
              style={{
                background: `radial-gradient(circle at ${spot.x}px ${spot.y}px, rgba(8, 3, 12, 0) 0, rgba(8, 3, 12, 0.3) 14vmax, rgba(8, 3, 12, 0.8) 55vmax)`,
              }}
            />
            {/* Kornblixtar i himlen medan det mörknar */}
            <div className="hol-final-preflash" />
            <div
              className="hol-final-whiteout"
              style={{
                background: `radial-gradient(circle at ${spot.x}px ${spot.y}px, rgba(255, 252, 244, 0.98) 0, rgba(255, 238, 214, 0.7) 22vmax, rgba(255, 226, 196, 0.32) 70vmax)`,
              }}
            />
          </div>,
          document.body
        )}
    </g>
  );
};

export default FinalStrike;
